import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/seo-companies-near-me-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/seo-companies-near-me-colorado")({
  head: () => ({
    meta: [
      { title: "SEO Companies Near Me: How to Choose One in Colorado (2026)" },
      { name: "description", content: "Searching 'SEO companies near me' in Colorado? Here's how to tell a real local SEO company from a call center — what to ask, what it costs, and red flags to avoid." },
      { property: "og:title", content: "SEO Companies Near Me: How to Choose One in Colorado (2026)" },
      { property: "og:description", content: "How to tell a real local SEO company from a call center — what to ask, what it costs, and the red flags." },
      { property: "og:url", content: `${SITE_URL}/learn/seo-companies-near-me-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "SEO Companies Near Me",
        description: ARTICLE?.desc ?? "How to choose a local SEO company in Colorado.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/seo-companies-near-me-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="SEO"
      read="10 min read"
      title="SEO companies near me: how to choose one in Colorado"
      intro="Type 'SEO companies near me' into Google and you get a mix of real local agencies, national companies with a local-sounding ad, and lead brokers who sell your info to five agencies at once. Here's how to tell them apart before you hand anyone money."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "SEO companies near me" },
      ]}
      path="/learn/seo-companies-near-me-colorado"
      faqs={[
        { q: "Does my SEO company need to be local?", a: "The work itself can be done from anywhere, but a company that actually knows Colorado knows which suburbs to target first, what locals call your trade, and how seasons change demand. A local company is also accountable — you can visit the office. Ours is at 3210 E Woodmen Rd in Colorado Springs." },
        { q: "Why do national companies show up for 'near me' searches?", a: "Because they buy ads on those searches and build location pages for every city in America. A Denver-sounding page doesn't mean a Denver office. Check the address on their Google Business Profile before you call." },
        { q: "What should a local SEO company charge?", a: "Real local SEO in Colorado runs $950 to $2,750 per month depending on how many cities and services you're targeting. Our plans: Starter $950/mo, Growth $1,650/mo, Expert $2,750/mo — month to month, no long contracts." },
      ]}
    >
      <h2>What &ldquo;near me&rdquo; results actually show you</h2>
      <p>
        Google fills &ldquo;near me&rdquo; searches from three buckets: paid ads at the top, the
        map pack, and the regular results below. The ads are whoever paid the most that day — not
        whoever is closest or best. The map pack is driven by Google Business Profiles, which is
        why a company&rsquo;s own profile tells you a lot: if an SEO company can&rsquo;t rank its
        own profile, it can&rsquo;t rank yours.
      </p>
      <p>
        Before you call anyone, check three things: their Google Business Profile (real address,
        real reviews), how long they&rsquo;ve been in business, and whether their own website
        ranks for anything. We&rsquo;ve been at this from Colorado Springs for 27 years — you can
        verify our office, hours, and reviews on our{" "}
        <Link to="/colorado-springs-office-woodmen-rd">Woodmen Rd office page</Link>.
      </p>

      <h2>Five questions that sort real companies from call centers</h2>
      <ul>
        <li><strong>Where is your office, and can I visit?</strong> A real local company answers instantly. A call center stalls or gives you a PO box.</li>
        <li><strong>What will you actually do in month one?</strong> Real answers name pages, profiles, and fixes. &ldquo;Optimization&rdquo; and &ldquo;submissions&rdquo; are not answers.</li>
        <li><strong>Do I own my website and content if I leave?</strong> The only correct answer is yes, in writing.</li>
        <li><strong>Can I cancel with 30 days&rsquo; notice?</strong> Long contracts exist to protect the agency, not you.</li>
        <li><strong>Who does the work?</strong> Some &ldquo;local&rdquo; agencies resell overseas fulfillment. Nothing wrong with specialists — but you should know who&rsquo;s touching your site.</li>
      </ul>

      <h2>The lead-broker trap</h2>
      <p>
        Some &ldquo;find an SEO company near me&rdquo; websites aren&rsquo;t companies at all —
        they&rsquo;re forms that sell your phone number to several agencies simultaneously. If a
        site asks for your info before telling you who they are, that&rsquo;s what&rsquo;s
        happening. Deal with a company that puts its own name, address, and phone number on the
        page.
      </p>

      <h2>What it should cost</h2>
      <p>
        For a single-market Colorado business, real local SEO starts around $950/month. Multi-city
        campaigns run $1,650–$2,750/month. Anything under $500/month is almost always software
        with a logo on it. See exactly what each tier includes on our{" "}
        <Link to="/pricing">pricing page</Link> — every price is published, no &ldquo;call for a
        quote&rdquo; games.
      </p>

      <h2>Get help with this</h2>
      <p><Link to="/seo-companies-near-me">Local SEO services from our Colorado Springs office</Link> explains our scope and the next step if you want this work done for your business.</p>

      <h2>What to do next</h2>
      <p>
        Read our checklist on <Link to="/learn/best-seo-companies-colorado">what makes the best
        SEO company</Link>, see how we handle <Link to="/colorado-seo">SEO across Colorado</Link>,
        or request a <Link to="/free-audit">free digital marketing audit</Link> — we&rsquo;ll show
        you where you stand before you spend anything.
      </p>
    </ArticleLayout>
  );
}
